import { fetchJson } from "../lib/api";

// Server-side carts (#547). Prices, best price and target come from the server;
// the client never computes a target of its own.

export type CartStatus = "open" | "requested" | "ordered" | "done";
export const CART_STATUS_LABELS: Record<CartStatus, string> = { open: "offen", requested: "angefragt", ordered: "bestellt", done: "erledigt" };
export type CostCenterType = "project" | "delivery_address" | "customer_reference";

export type CartSupplierPrice = {
  supplier: string;
  uom: string | null;
  price: string | null;
  doc_id: string | null;
  doc_number: string | null;
  doc_date: string | null;
  unit_unclear: boolean;
};

export type CartLine = {
  line_id: string;
  position: number;
  /** Null for a special position (#556): a one-off item is never an article. */
  article_group_key: string | null;
  /** A one-off item with an agreed price, no reference and no judgement. */
  is_special: boolean;
  article_code: string | null;
  description: string | null;
  qty: string;
  supplier: string | null;
  previous_supplier: string | null;
  agreed_price: string | null;
  added_reference_price: string | null;
  added_at: string;
  unit_price: string | null;
  uom: string | null;
  reference: CartSupplierPrice | null;
  target_price: string | null;
  has_reference: boolean;
  best: CartSupplierPrice | null;
  /** Niedrigster Rechnungspreis der letzten 12 Monate, gleiche Einheit, auch bei nur einem Großhändler (#630). */
  lowest_price: { supplier: string; price: string; doc_id: string; doc_number: string | null; doc_date: string } | null;
  /** Wholesalers with a price in the same unit; a judgement needs at least two. */
  comparable_count: number;
  cheaper_elsewhere: boolean;
  best_delta_pct: string | null;
  total: string | null;
  saving_at_best: string | null;
  suppliers: CartSupplierPrice[];
};

export type CartHeader = {
  cart_id: string;
  owner: { user_id: string; name: string; is_me: boolean };
  name: string;
  status: CartStatus;
  cost_center: { type: CostCenterType; key: string; label: string } | null;
  delivery_address: { key: string; label: string } | null;
  revision: number;
  created_at: string;
  updated_at: string;
  line_count: number;
  /** Who marked the cart as asked for, and when (#548); null while it is only open. */
  status_marked: { at: string; by: string } | null;
  /** How far back the Bestpreis looks; reference and target stay on 12 months. */
  best_price_months: BestPriceMonths;
};

export const BEST_PRICE_MONTHS = [6, 12, 24, 36] as const;
export type BestPriceMonths = (typeof BEST_PRICE_MONTHS)[number];

/** "der letzten 2 Jahre" (genitive) or "aus den letzten 2 Jahren" (dative). */
export function bestPriceWindowLabel(months: number, grammaticalCase: "genitive" | "dative" = "genitive"): string {
  const years = months % 12 === 0 && months > 12;
  const unit = years ? "Jahre" : "Monate";
  return `${years ? months / 12 : months} ${grammaticalCase === "dative" ? `${unit}n` : unit}`;
}

export type Cart = CartHeader & {
  lines: CartLine[];
  groups: { supplier: string | null; line_ids: string[]; total: string; complete: boolean }[];
  total: string;
  total_complete: boolean;
  saving_at_best: string;
  rule: { basis: "last" | "best"; discount_pct: string; revision: number };
  can_edit: boolean;
};

export type CartList = { items: CartHeader[]; can_create: boolean };

export const cartsQueryKey = (scope: "mine" | "company", owner?: string | null) =>
  ["carts", "list", scope, owner ?? null] as const;
export const cartQueryKey = (cartId: string) => ["carts", "detail", cartId] as const;

export function fetchCarts(scope: "mine" | "company", owner?: string | null, status?: CartStatus): Promise<CartList> {
  const params = new URLSearchParams({ scope });
  if (owner) params.set("owner", owner);
  if (status) params.set("status", status);
  return fetchJson<CartList>(`/api/carts?${params}`);
}

export const fetchCart = (cartId: string) => fetchJson<Cart>(`/api/carts/${encodeURIComponent(cartId)}`);

function send<T>(url: string, method: string, body?: unknown): Promise<T> {
  return fetchJson<T>(url, { method, ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
}

export const createCart = (name?: string) => send<Cart>("/api/carts", "POST", { name: name ?? null });

/** Atomic, one-time migration; a deleted result stays completed. */
export const importLegacyCart = (items: { article_group_key: string; qty: number }[]) =>
  send<{ cart_id: string | null }>("/api/carts/import-legacy", "POST", { items });

export type CartUpdate = {
  name?: string;
  cost_center?: { type: CostCenterType; key: string };
  clear_cost_center?: boolean;
  delivery_address_key?: string;
  clear_delivery_address?: boolean;
  best_price_months?: BestPriceMonths;
};

export const updateCart = (cart: Pick<Cart, "cart_id" | "revision">, update: CartUpdate) =>
  send<Cart>(`/api/carts/${encodeURIComponent(cart.cart_id)}`, "PATCH", { expected_revision: cart.revision, ...update });

export type SpecialLineInput = {
  description: string;
  qty: number;
  uom?: string | null;
  agreed_price: number;
  supplier?: string | null;
};

export const addSpecialCartLine = (cart: Pick<Cart, "cart_id" | "revision">, line: SpecialLineInput) =>
  send<Cart>(`/api/carts/${encodeURIComponent(cart.cart_id)}/special-lines`, "POST", {
    expected_revision: cart.revision,
    ...line,
  });

export const addCartLines = (cart: Pick<CartHeader, "cart_id" | "revision">, items: { article_group_key: string; qty: number }[]) =>
  send<Cart>(`/api/carts/${encodeURIComponent(cart.cart_id)}/lines`, "POST", { expected_revision: cart.revision, items });

export type CartLineUpdate = {
  qty?: number;
  supplier?: string;
  undo_move?: boolean;
  agreed_price?: number;
  clear_agreed_price?: boolean;
};

export const updateCartLine = (cart: Pick<Cart, "cart_id" | "revision">, lineId: string, update: CartLineUpdate) =>
  send<Cart>(`/api/carts/${encodeURIComponent(cart.cart_id)}/lines/${encodeURIComponent(lineId)}`, "PATCH", {
    expected_revision: cart.revision,
    ...update,
  });

export const removeCartLine = (cart: Pick<Cart, "cart_id" | "revision">, lineId: string) =>
  send<Cart>(
    `/api/carts/${encodeURIComponent(cart.cart_id)}/lines/${encodeURIComponent(lineId)}?expected_revision=${cart.revision}`,
    "DELETE",
  );

export const distributeCartToBest = (cart: Pick<Cart, "cart_id" | "revision">) =>
  send<Cart>(`/api/carts/${encodeURIComponent(cart.cart_id)}/distribute-best`, "POST", { expected_revision: cart.revision });

export type ConfirmedOrder = { order_id: string; reference: string; supplier: string | null; total: string; line_count: number };

/** Turns the cart into one order per wholesaler (#554); TruuCost never sends it. */
export const confirmCart = (cart: Pick<Cart, "cart_id" | "revision">) =>
  send<{ cart: Cart; orders: ConfirmedOrder[] }>(`/api/carts/${encodeURIComponent(cart.cart_id)}/confirm`, "POST", {
    expected_revision: cart.revision,
  });

export const setCartStatus = (cart: Pick<Cart, "cart_id" | "revision">, status: "open" | "requested") =>
  send<Cart>(`/api/carts/${encodeURIComponent(cart.cart_id)}/status`, "POST", { expected_revision: cart.revision, status });

export const deleteCart = (cart: Pick<CartHeader, "cart_id" | "revision">) =>
  send<{ deleted: string }>(`/api/carts/${encodeURIComponent(cart.cart_id)}?expected_revision=${cart.revision}`, "DELETE");

export const cartUrl = (cartId?: string | null) =>
  cartId ? `/warenkorb?cart=${encodeURIComponent(cartId)}` : "/warenkorb";
